import React, {useMemo, useRef, useState} from 'react'
import {useAuth} from "../../../providers/AuthProvider";
import Form, {FormInputComponent} from "../../../components/form/Form";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import User from "../../../models/User";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {Checkbox, Empty, Input, Popover} from "antd";
import SupplierTalentResourceCard from "./SupplierTalentResourceCard";
import {Link, Navigate, Route, Routes, useNavigate, useParams} from "react-router-dom";
import Client from "../../../models/Client";
import PageNotAllowed from "../../../components/errors/PageNotAllowed";
import LoadingBanner from "../../../components/LoadingBanner";
import ErrorBanner from "../../../components/ErrorBanner";
import ResourceNotFound from "../../../components/errors/ResourceNotFound";
import {useAPI} from "../../../config/API";
import {InfoCircleOutlined} from "@ant-design/icons";
import InternationPhoneInput from "../../../components/form/InternationPhoneInput";

const inviteTalentResource = async({client_id, params}) => {

    params.role = parseInt(TB.invert(laravel.client_user_roles)[Client.Roles.TalentResource])

    params.default_hourly_rate = parseFloat(params.default_hourly_rate);

    const InviteTalentRequest = TB.request(`/api/clients/${client_id}/users/create`, params);

    let response = await InviteTalentRequest.post().catch(console.warn);
    if (!response && InviteTalentRequest.didFail) {
        if (InviteTalentRequest.response?.error?.data?.email) {
            throw new Error("Email Taken")
        }
        throw new Error("Something Went Wrong")
    }
    return response
}

const updateTalentResource = async({client_id, talent_user_id, params}) => {

    params.role = parseInt(TB.invert(laravel.client_user_roles)[Client.Roles.TalentResource])

    params.default_hourly_rate = parseFloat(params.default_hourly_rate);

    const UpdateTalentRequest = TB.request(`/api/clients/${client_id}/users/${talent_user_id}/update`, params);
    let response = await UpdateTalentRequest.post().catch(console.warn);
    if (!response && UpdateTalentRequest.didFail) {
        if (UpdateTalentRequest.response?.error?.data?.email) {
            throw new Error("Email Taken")
        }
        throw new Error("Something Went Wrong")
    }
    return response
}

const MemberForm = React.forwardRef(({member = null, isUpdating, onSubmit, submitButton, ...props}, formRef) => {
        const {auth} = useAuth();
        const [formModel, setFormModel] = useState(() => {
            let model = member ? {...member} : ({
                location: auth.clientOrg?.()?.location || null,
                offers_service_work: true,
                offers_byo_work: true
            })

            if (model.phone) {
                let phoneNumber = model.phone.replace(/\D/g, '');
                if (phoneNumber?.length === 10 && !model.phone.includes('+')) {
                    model.phone = "+1 " + model.phone;
                }
            }
            return model;
        });

        const [popupOpen, setPopupOpen] = useState(false);


        return (
            <div className="talent-role-form">

                <Form
                    ref={formRef}
                    showRequired
                    // minimal
                    title={null}
                    subtitle={null}
                    submitButton={false}
                    className="default-form"
                    onSubmit={(model) => onSubmit(formModel)}
                    background="transparent"
                    model={formModel}
                    onChange={setFormModel}
                    fields={[]}
                    isDisabled={isUpdating || props.isDisabled}>

                    <div className="flex flex-col items-stretch">
                        <div className="field-section">
                            <div className="field">
                                <div className="field-element">
                                    <h5 className="form-header">Name & Contact Info</h5>
                                </div>
                            </div>
                        </div>
                        <div className="field-section flex flex-row items-start">

                            <div className="field px-3.5 basis-5/12">
                                <FormInputComponent
                                    type="text"
                                    fieldName="first_name"
                                    value={formModel.first_name || ''}
                                    disabled={!!member}
                                    maxLength={250}
                                    label="First Name"
                                    placeholder="First name"
                                    required/>
                            </div>
                            <div className="field px-3.5 basis-5/12">
                                <FormInputComponent
                                    type="text"
                                    fieldName="last_name"
                                    value={formModel.last_name || ''}
                                    disabled={!!member}
                                    maxLength={250}
                                    label="Last Name"
                                    placeholder="Last name"
                                    required/>
                            </div>
                        </div>

                        <div className="field-section flex flex-row items-start">
                            <div className="field px-3.5 basis-6/12">
                                <FormInputComponent
                                    type="antd-input"
                                    fieldName="email"
                                    value={formModel.email || ''}
                                    disabled={!!member}
                                    placeholder="Enter email"
                                    required
                                    invalidStyles={{top: '100%'}}
                                    labelClassName="flex flex-row items-center gap-1.5"
                                    label={(
                                        <Popover color={"#fff"} placement="topLeft"
                                                 trigger="hover"
                                                 open={popupOpen}
                                                 onOpenChange={isOpen => setPopupOpen(isOpen)}
                                                 content={(
                                                     <p className="text-center max-w-[530px]">
                                                         <span className="pr-1.5">
                                                             <InfoCircleOutlined className="text-sm"/>
                                                         </span>
                                                         This email address will be utilized to access the <b>individual
                                                         talent account and profile for this member within your agency</b>.
                                                         The email address must be unique and can not be shared with an
                                                         agency portal client account.
                                                     </p>
                                                 )}>
                                            <div className="flex flex-row items-center">
                                                <span className="mr-1.5">
                                                    <InfoCircleOutlined className="text-sm"/>
                                                </span>
                                                <span>
                                                    Email
                                                </span>
                                            </div>

                                        </Popover>
                                    )}
                                >

                                    <Input type="email" onFocus={e => {
                                        setPopupOpen(true)
                                    }} onBlur={e => {
                                        setPopupOpen(false)
                                    }}/>
                                </FormInputComponent>

                            </div>
                        </div>
                        <div className="field-section flex flex-row items-start">
                            <div className="field px-3.5 basis-6/12">
                                <FormInputComponent
                                    type="antd-input"
                                    fieldName="phone"
                                    value={formModel.phone || ''}
                                    disabled={!!member}
                                    label="Phone"
                                    placeholder="555-555-5555"
                                    required={false}>
                                    <InternationPhoneInput/>
                                </FormInputComponent>
                            </div>
                        </div>

                    </div>

                    <hr className="my-3"/>

                    <div className="flex flex-col items-start">
                        <div className="field-section">
                            <div className="field">
                                <div className="field-element">
                                    <h5 className="form-header">Profile Info & Preferences</h5>
                                    <p className="form-subheader">
                                        Add profile info and set an ideal hourly rate to use as a default for this
                                        resource's roles.
                                        Note: You can set different rates per role later by editing them in the talent's
                                        profile.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="field-section flex flex-row items-start">
                            <div className="field px-3.5 basis-8/12">
                                <FormInputComponent
                                    type="location"
                                    fieldName="location"
                                    value={formModel.location || ''}
                                    label="Location"
                                    placeholder="Add a location"
                                    required={false}/>
                            </div>
                        </div>
                        <div className="field-section flex flex-row items-start">
                            <div className="field px-3.5 basis-6/12">
                                <FormInputComponent
                                    type="text"
                                    fieldName="job_title"
                                    value={formModel.job_title || ''}
                                    label="Job Title"
                                    maxLength={50}
                                    placeholder="UX Designer"
                                    required/>
                            </div>
                        </div>

                        <div className="field-section flex flex-row items-start">
                            <div className="field px-3.5 basis-6/12">
                                <FormInputComponent
                                    type="number"
                                    fieldName="default_hourly_rate"
                                    value={formModel.default_hourly_rate || ''}
                                    label="Default hourly rate"
                                    placeholder={null}
                                    addonBefore="$"
                                    min={1}
                                    max={1000}
                                    required={true}
                                    validation={{
                                        rules: {
                                            isInt: true,
                                            min: (values, value) => !TB.isDefined(value) || parseInt(value) >= 0,
                                            max: (values, value) => !TB.isDefined(value) || parseInt(value) <= 1000,
                                        },
                                        errors: {
                                            min: "The rate must be greater than $0",
                                            max: "The rate must be less than $1000",
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className="p-4">

                            <FormInputComponent
                                type="antd-input"
                                fieldName="offers_byo_work"
                                value={formModel.offers_byo_work || false}
                                label={false}
                                placeholder={null}
                                disabled={props.isDisabled || isUpdating}
                                required={false}
                            >
                                <Checkbox
                                    checked={formModel.offers_byo_work}>
                                    <p className="text-xs">Available for hire by role</p>
                                </Checkbox>
                            </FormInputComponent>

                            <FormInputComponent
                                type="antd-input"
                                fieldName="offers_service_work"
                                value={formModel.offers_service_work || false}
                                label={false}
                                placeholder={null}
                                disabled={props.isDisabled || isUpdating}
                                required={false}
                            >
                                <Checkbox
                                    checked={formModel.offers_service_work}>
                                    <p className="text-xs">Available for hire by team</p>
                                </Checkbox>
                            </FormInputComponent>
                        </div>
                    </div>

                    <div className="self-end mt-4 flex flex-row justify-start items-center gap-3">

                        <Link
                            className="btn btn-dark" type="button"
                            to=".."
                            disabled={props.isDisabled || isUpdating}>
                            Cancel
                        </Link>

                        <button
                            className="btn btn-primary" type="submit" formNoValidate
                            disabled={props.isDisabled || isUpdating}>
                            {submitButton || 'Invite talent resource'}
                        </button>
                    </div>
                </Form>
            </div>
        )
    }
)
const EditStep = (props) => {
    const {auth} = useAuth();
    const navigate = useNavigate();
    const {talentUserId} = useParams();

    const queryClient = useQueryClient();

    const {
        data: talentUser,
        isInitialLoading,
        isRefetching,
        refetch,
        error,
    } = useAPI(['fetchSupplierTalentResource', parseInt(talentUserId)],
        {
            enabled: auth?.canManageClientResourceTalent() && auth?.hasSupplierClientOrg() && !TB.isNaN(parseInt(talentUserId)),
        }
    );
    const formRef = useRef(null);

    const editingUser = useMemo(() => {
        if (!talentUser) {
            return null
        }
        return TB.pick({...talentUser}, [
            'first_name',
            'last_name',
            'email',
            'phone',
            'location',
            'job_title',
            'default_hourly_rate',
            'offers_byo_work',
            'offers_service_work',
        ])
    }, [talentUser, isInitialLoading, isRefetching]);

    const updateTalentResourceMutation = useMutation(['updateTalentResource'],
        params => updateTalentResource({client_id: auth.clientOrg?.()?.id, talent_user_id: talentUserId, params}),
        {
            onSuccess: async(response) => {
                console.log('onSuccess', response);
                if (response.success?.data?.user) {
                    let updatedMember = User.make(response.success.data.user);
                    queryClient.setQueriesData(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id], prevData => {
                        if (!prevData) {
                            return prevData;
                        }

                        return TB.map(prevData || [], member => {
                            if (member.id === updatedMember.id){
                                return updatedMember;
                            }
                            return member;
                        })
                    })
                }

                TB.toast('Talent Resource Updated');
                return navigate('..', {replace: true})
            },
            onError: async (error) => {
                if (error?.message === "Email Taken") {
                    await formRef.current?.updateInputsWithError({email: "Email already taken"});

                    return TB.alert("alert", "Email Already Taken", `The email address entered is already being used by another Wripple account. Please type
                            another email address to use for the talent member you are inviting. <b>Note: agency portal
                            client accounts and individual talent accounts can not share the same email address.</b>`, {title: "Ok"})
                } else {
                    return TB.showServerErrorToast();
                }
            }
        })


    if (auth && (!auth.canManageClientResourceTalent() || !auth.hasSupplierClientOrg())) {
        return <PageNotAllowed/>
    }

    if (isInitialLoading || isRefetching) {
        return (
            <div className="flex flex-col pt-8 items-center justify-center">
                <LoadingBanner message="Loading"/>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex flex-col pt-8 items-center justify-center">
                <ErrorBanner title="Something went wrong" message="Please try again later" onRetry={refetch}/>
            </div>
        )
    }
    if (!talentUser) {
        return <ResourceNotFound type="Talent Resource"/>
    }

    return (
        <div className="supplier-talent-edit-form">
            <div className="form-title-section">
                <div className="step-title-content">
                    <div>
                        <div className="flex items-center pb-4">
                            <h3 className="form-title !mb-0">
                                Update Talent Resource
                            </h3>
                        </div>

                    </div>
                </div>
            </div>

            <MemberForm
                {...props}
                ref={formRef}
                member={editingUser}
                submitButton="Update talent resource"
                isUpdating={updateTalentResourceMutation.isLoading}
                onSubmit={(model) => updateTalentResourceMutation.mutate(model)}/>
        </div>
    )
}

const InviteStep = (props) => {

    const {auth} = useAuth();
    const navigate = useNavigate();

    const queryClient = useQueryClient();
    const inviteTalentResourceMutation = useMutation(['inviteTalentResource'],
            params => inviteTalentResource({client_id: auth.clientOrg?.()?.id, params}),
        {
            onSuccess: async(response) => {
                console.log('onSuccess', response);

                await queryClient.setQueriesData(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id], prevData => {
                    if (!prevData) {
                        return prevData;
                    }
                    return [...(prevData || []), User.make(response.success.data.user)]
                })


                TB.toast('Talent Resource Invite Sent');
                return navigate('..', {replace: true})
            },
            onError: async (error) => {
                if (error?.message === "Email Taken") {
                    await formRef.current?.updateInputsWithError({email: "Email already taken"});

                    return TB.alert("alert", "Email Already Taken", `The email address entered is already being used by another Wripple account. Please type
                            another email address to use for the talent member you are inviting. <b>Note: agency portal
                            client accounts and individual talent accounts can not share the same email address.</b>`, {title: "Ok"})
                } else {
                    return TB.showServerErrorToast();
                }
            }
    })


    const formRef = useRef(null);


    return (
        <div>
            <div className="form-title-section">
                <div className="step-title-content">
                    <div>
                        <div className="flex items-center pb-4">
                            <h3 className="form-title !mb-0">
                               Invite New Talent Resource
                            </h3>
                        </div>

                        <h5 className="form-subtitle">
                            Use the form below to invite a new talent resource to your agency.
                            You will be able to edit their full profile once they have accepted the invite via email and registered their account.
                        </h5>

                    </div>
                </div>
            </div>

            <MemberForm
                {...props}
                ref={formRef}
                submitButton="Invite talent resource"
                isUpdating={inviteTalentResourceMutation.isLoading}
                onSubmit={(model) => inviteTalentResourceMutation.mutate(model)}/>
        </div>
    )
}

const ManageResourcesStep = () => {
    const {auth, update: updateAuth} = useAuth();

    const {
        data: members,
        isFetching,
        isInitialLoading: isLoading,
        error,
        refetch
    } = useAPI(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id],  {
        onSuccess: async (data) => {
            updateAuth({
                ...auth,
                client_organization: {
                    ...auth.clientOrg(),
                    total_supplied_talent_users: data.length,
                    total_supplied_matching_talent_users: TB.filter(data, {'is_available': true, offers_byo_work: true})?.length,
                    total_supplied_team_talent_users: TB.filter(data, {'is_available': true, offers_service_work: true})?.length
                }
            })
        },
        enabled: !!auth && !!auth.clientOrg?.()?.id
    });

    const [inactiveMembers,invitedMembers, activeMembers] = useMemo(() => {
        let users = TB.orderBy(members, 'added_at', 'desc');
        let [nonActive, active] = TB.partition(users, ['is_active', false]);
        let [archived, invited] = TB.partition(nonActive, ['status', 'Archived'])
        return [archived, invited, active]
    }, [members, isLoading]);

    return (
        <div className="flex flex-col items-stretch">
            <div className="form-title-section">
                <div className="step-title-content">
                    <div>
                        <div className="flex items-center pb-4">
                            <h3 className="form-title !mb-0">
                                Talent Resources
                            </h3>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-between sm:items-center pb-4">
                            <h5 className="form-subtitle">
                               Manage your agency's resources
                            </h5>
                            {members?.length > 0 && (
                                <Link to="invite" replace className="inline btn btn-primary self-end mb-5">
                                    Invite New Member
                                </Link>
                            )}

                        </div>

                    </div>
                </div>
            </div>

            {isLoading && (
                <div className="flex-1 w-full py-3 flex flex-col items-center justify-center">
                    <LoadingSpinner />
                </div>
            )}
            {!isLoading && error && (
                <div className="self-center">
                    <Empty
                        image={(
                            <i className="fa fa-exclamation-circle fa-3x text-gray-400" />
                        )}
                        imageStyle={{ height: 'auto' }}
                        description={
                            <span>
                                Something went wrong
                            </span>
                        }
                    >
                        <button className="mx-auto min-w-[80px] btn btn-dark" onClick={refetch}>Retry</button>
                    </Empty>
                </div>
            )}
            {!isLoading && !error && (members.length > 0 ? (
                <div className="flex flex-col gap-10 items-stretch">
                    {invitedMembers?.length > 0 && (
                        <div className="flex flex-col gap-6 items-stretch">
                            <p className="text-base font-semibold text-left">
                                <span className="text-[#5f3db1]">
                                    Awaiting Registration
                                </span>
                                &nbsp;&nbsp;
                                <span className="text-[#9374dc]">
                                    ({invitedMembers.length})
                                </span>
                            </p>
                            <div className="flex flex-col gap-5 items-stretch">
                                {TB.map(invitedMembers, member => (
                                    <SupplierTalentResourceCard key={member.id} talent={member} isDisabled={isFetching}/>
                                ))}
                            </div>
                        </div>
                    )}
                    {activeMembers?.length > 0 && (
                        <div className="flex flex-col gap-6 items-stretch">
                            <p className="text-base font-semibold text-left">
                                <span className="text-[#5f3db1]">
                                    Active
                                </span>
                                &nbsp;&nbsp;
                                <span className="text-[#9374dc]">
                                    ({activeMembers.length})
                                </span>
                            </p>
                            <div className="flex flex-col gap-5 items-stretch">
                                {TB.map(activeMembers, member => (
                                    <SupplierTalentResourceCard key={member.id} talent={member} isDisabled={isFetching}/>
                                ))}
                            </div>
                        </div>
                    )}
                    {inactiveMembers?.length > 0 && (
                        <div className="flex flex-col gap-6 items-stretch">
                            <p className="text-base font-semibold text-left">
                                <span className="text-[#5f3db1]">
                                    Deactivated
                                </span>
                                &nbsp;&nbsp;
                                <span className="text-[#9374dc]">
                                    ({inactiveMembers.length})
                                </span>
                            </p>
                            <div className="flex flex-col gap-5 items-stretch">
                                {TB.map(inactiveMembers, member => (
                                    <SupplierTalentResourceCard key={member.id} talent={member} isDisabled={isFetching}/>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white px-4 py-5 flex flex-col items-center justify-center">
                    <Empty description="No members invited yet" image={Empty.PRESENTED_IMAGE_SIMPLE}>

                        <Link to="invite" replace={true} className="mx-auto btn btn-primary">
                            Invite Member
                        </Link>
                    </Empty>
                </div>
            ))}
        </div>
    )
}
const SupplierTalentResourcesStep = ({supplierOrg, onChange, ...props}) => {

    const {auth} = useAuth();

    return (
        <div>
            <Routes>
                <Route index element={<ManageResourcesStep/>}/>
                <Route path={'invite'} element={<InviteStep/>}/>
                <Route path={'edit/:talentUserId'} element={<EditStep/>}/>
                <Route path={'*'} element={<Navigate to="." replace={true}/>}/>
            </Routes>
        </div>
    )
}
export default SupplierTalentResourcesStep

const styles = {
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: '0 15px',
    },
};